import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import anime from "animejs";
import PopupButton from "../Buttons/PopupButton";
import {
  closeTurboSuggestionPopup,
  disableManualStopTracking,
  setAutoplaySpeed,
} from "../../../../State/actions";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { getUITexture } from "../../../../assets";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";

import { FEATURES_CONFIG } from "../../../../featuresConfig";
import StorageManager from "../../../../utils/StorageManager";

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Popup configuration — same sizing as WarningPopup/InfoPopup
const TURBO_POPUP_CONFIG = {
  basePadding: 40,
  baseMessageFontSize: 24,
  baseButtonFontSize: 24,
  baseTextButtonGap: 40,
  baseContentWidth: 480,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.55 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.6 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.75 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.0 },
    },
  },
};

const TurboSuggestionPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { dimensions, device, orientation } = useDimensions();
  const { turboSuggestionPopupActive } = useSelector(
    (state) => state.userInterface,
  );

  const isOpen = turboSuggestionPopupActive;

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const popupContentRef = useRef(null);



  const popupMessage = t("turbo_suggestion_message", { ns: "common" });

  const deviceConfig = useMemo(() => {
    return (
      TURBO_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
      TURBO_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][
        PixiSettings.ORIENTATION.LANDSCAPE
      ]
    );
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  const frameTexture = useMemo(
    () => getUITexture("popup_frame_horizontal"),
    [],
  );

  const popupScale = useMemo(() => {
    const deviceScale = deviceConfig.scaleMultiplier || 1.0;
    if (!frameTexture) return deviceScale;

    const idealContentWidth = TURBO_POPUP_CONFIG.baseContentWidth * deviceScale;
    const idealPadding = TURBO_POPUP_CONFIG.basePadding * deviceScale;
    const idealPanelWidth = idealContentWidth + idealPadding * 2;
    const scaleX = idealPanelWidth / frameTexture.width;
    const idealFrameWidth = frameTexture.width * scaleX;
    const idealFrameHeight = frameTexture.height * scaleX;

    const maxWidth = dimensions.width * 0.8;
    const maxHeight = dimensions.height * 0.8;
    const widthRatio = idealFrameWidth > maxWidth ? maxWidth / idealFrameWidth : 1;
    const heightRatio = idealFrameHeight > maxHeight ? maxHeight / idealFrameHeight : 1;
    const clampRatio = Math.min(widthRatio, heightRatio);

    return deviceScale * clampRatio;
  }, [deviceConfig, frameTexture, dimensions.width, dimensions.height]);

  const padding = useMemo(
    () => TURBO_POPUP_CONFIG.basePadding * popupScale,
    [popupScale],
  );
  const messageFontSize = useMemo(
    () => Math.round(TURBO_POPUP_CONFIG.baseMessageFontSize * popupScale),
    [popupScale],
  );
  const textButtonGap = useMemo(
    () => TURBO_POPUP_CONFIG.baseTextButtonGap * popupScale,
    [popupScale],
  );
  const contentWidth = useMemo(
    () => TURBO_POPUP_CONFIG.baseContentWidth * popupScale,
    [popupScale],
  );

  // Button sized proportionally to content width
  const buttonTexture = useMemo(() => getUITexture("popup_button"), []);
  const buttonWidth = useMemo(() => contentWidth * 0.25, [contentWidth]);
  const buttonHeight = useMemo(() => {
    if (!buttonTexture) return buttonWidth * 0.5;
    const aspect = buttonTexture.height / buttonTexture.width;
    return buttonWidth * aspect;
  }, [buttonTexture, buttonWidth]);

  const messageStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: messageFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
      }),
    [messageFontSize, contentWidth],
  );

  const textMetrics = useMemo(
    () => TextMetrics.measureText(popupMessage, messageStyle),
    [popupMessage, messageStyle],
  );
  const textHeight = useMemo(
    () => textMetrics.height,
    [textMetrics.height],
  );



  // Frame scaling
  const desiredPanelWidth = useMemo(
    () => contentWidth + padding * 2,
    [contentWidth, padding],
  );
  const desiredPanelHeight = useMemo(
    () => padding + textHeight + textButtonGap + buttonHeight + padding,
    [textHeight, textButtonGap, buttonHeight, padding],
  );

  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    const scaleX = desiredPanelWidth / frameTexture.width;
    const scaleY = desiredPanelHeight / frameTexture.height;
    return Math.max(scaleX, scaleY);
  }, [frameTexture, desiredPanelWidth, desiredPanelHeight]);

  const frameSpriteWidth = useMemo(
    () => (frameTexture ? frameTexture.width * frameScale : 0),
    [frameTexture, frameScale],
  );
  const frameSpriteHeight = useMemo(
    () => (frameTexture ? frameTexture.height * frameScale : 0),
    [frameTexture, frameScale],
  );

  // Button anchored to bottom with padding
  const buttonYPos = useMemo(
    () => frameSpriteHeight / 2 - padding - buttonHeight / 2,
    [frameSpriteHeight, padding, buttonHeight],
  );

  // Text centered in space between top padding and button
  const textAreaTop = useMemo(
    () => -frameSpriteHeight / 2 + padding,
    [frameSpriteHeight, padding],
  );
  const textAreaBottom = useMemo(
    () => buttonYPos - textButtonGap,
    [buttonYPos, textButtonGap],
  );
  const messageY = useMemo(
    () => (textAreaTop + textAreaBottom) / 2,
    [textAreaTop, textAreaBottom],
  );

  const yesText = t("yes", { ns: "common" });
  const noText = t("no", { ns: "common" });

  // Two buttons side by side
  const yesButtonX = useMemo(
    () => -buttonWidth / 2 - padding * 0.5,
    [buttonWidth, padding],
  );
  const noButtonX = useMemo(
    () => buttonWidth / 2 + padding * 0.5,
    [buttonWidth, padding],
  );

  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  // Close with decline action — persist so it never shows again
  const handleDecline = useCallback(() => {
    if (isClosing) return;
    dispatch(disableManualStopTracking());

    // Save decline to localStorage so popup won't appear again
    const uiSettings = StorageManager.loadUISettings();
    StorageManager.saveUISettings({
      ...uiSettings,
      turboSuggestionDeclined: true,
    });

    setIsClosing(true);
  }, [isClosing, dispatch]);

  // Accept turbo
  const handleAccept = useCallback(() => {
    if (isClosing) return;
    dispatch(disableManualStopTracking());
    dispatch(setAutoplaySpeed("turbo"));
    setIsClosing(true);
  }, [isClosing, dispatch]);



  // Open animation
  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsVisible(true);
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (!isOpen && !isClosing) {
      setIsVisible(false);
      setOverlayAlpha(0);
      setHasAnimated(false);
    }
  }, [isOpen, isClosing]);

  // Close animation
  useEffect(() => {
    if (!isClosing) return;
    let overlayAnim = null;
    let popupAnim = null;

    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
      anime({
        targets: popupContentRef.current.scale,
        x: 0.85,
        y: 0.85,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
    }

    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: POPUP_ANIM_DURATION,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeTurboSuggestionPopup());
        setIsClosing(false);
        setIsVisible(false);
        setHasAnimated(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosing, dispatch]);

  // Don't render if turbo suggestion feature is disabled
  if (!FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
    return null;
  }

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Container
      name="turbo_suggestion_popup"
      zIndex={10000}
      sortableChildren={true}
    >
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => {
          e.stopPropagation();
        }}
        zIndex={10000}
      />

      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
      >
        {/* Frame background */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Suggestion message */}
        <Text
          text={popupMessage}
          style={messageStyle}
          anchor={0.5}
          x={0}
          y={messageY}
          resolution={2}
        />

        {/* Yes button (left) */}
        <PopupButton
          x={yesButtonX}
          y={buttonYPos}
          text={yesText}
          onClick={handleAccept}
          width={buttonWidth}
          height={buttonHeight}
          accessibleTitle={yesText}
        />

        {/* No button (right) */}
        <PopupButton
          x={noButtonX}
          y={buttonYPos}
          text={noText}
          onClick={handleDecline}
          width={buttonWidth}
          height={buttonHeight}
          accessibleTitle={noText}
        />
      </Container>
    </Container>
  );
};

export default TurboSuggestionPopup;
